import { useState } from 'react'
import { PartnerService } from '@duck/api-definitions'
import { useTar } from '@/modules/i18n'
import { apis } from '@/modules/apiClients'
import { toast } from 'sonner'

interface UseAssignmentActionsProps {
  // Only id + status are consumed, so callers without a full Assignment
  // (e.g. admin user details rows) can pass a narrowed object.
  assignment: Pick<PartnerService.Assignment, 'id' | 'status'>
  onAssignmentChange?: () => void
}

export const useAssignmentActions = ({ assignment, onAssignmentChange }: UseAssignmentActionsProps) => {
  const { t } = useTar()
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)

  const handleEdit = () => setIsEditDialogOpen(true)

  const handleDelete = async () => {
    try {
      await apis.partnerService.assignmentsAssignmentIdDelete(assignment.id)
      toast.success(t('common.assignmentDeleted'))
      onAssignmentChange?.()
    } catch (error) {
      console.error('Failed to delete assignment:', error)
      toast.error(t('errors.errorDeletingAssignment'))
    }
  }

  // Admin-only: un-deletes a soft-deleted assignment
  const handleRestore = async () => {
    try {
      await apis.partnerService.assignmentsAssignmentIdRestorePatch(assignment.id)
      toast.success(t('common.assignmentRestored'))
      onAssignmentChange?.()
    } catch (error) {
      console.error('Failed to restore assignment:', error)
      toast.error(t('errors.errorRestoringAssignment'))
    }
  }

  const handleSetStatus = async (status: PartnerService.AssignmentStatus) => {
    try {
      await apis.partnerService.assignmentsAssignmentIdStatusPatch(assignment.id, { status })
      toast.success(
        status === PartnerService.AssignmentStatus.Active
          ? t('partner.activateAssignment')
          : t('partner.deactivateAssignment')
      )
      onAssignmentChange?.()
    } catch (error) {
      console.error('Failed to update assignment status:', error)
      toast.error(t('errors.errorUpdatingAssignment'))
    }
  }

  const isActive = assignment.status === PartnerService.AssignmentStatus.Active

  return {
    handleEdit,
    handleDelete,
    handleRestore,
    handleSetStatus,
    isActive,
    isEditDialogOpen,
    setIsEditDialogOpen,
    isDeleteDialogOpen,
    setIsDeleteDialogOpen,
  }
}
